import { Alert, Button } from "reactstrap";
import { Module } from "../../../types/TenantInterfaces";
import AudioRecorder from "../../../components/Sidebar/QuickNote/QuickNoteRecording/AudioRecordingUtils";
import { useState } from "react";
import useTranscript from "../../../api/api-hooks/tools/useTranscript";
import IconButton from "../../../elements/IconButton";
import AppSpinner from "../../../elements/AppSpinner";
import AIToolsAPI from "../../../api/api/AIToolsAPI";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";
import AppModal from "../../../components/Modals/AppModal";
import useFieldMap from "../../../hooks/useFieldMap";
import FieldFormatter from "../../../components/Records/Formatters/FieldFormatter";
import { findWhere } from "underscore";
import useUpdateRecord from "../../../api/api-hooks/records/useUpdateRecord";
import { handleLocationSelection } from "../../../components/Records/Location/LocationHelperFunctions";
import StrUtils from "../../../utils/StrUtils";
import BrieIconOutlined from "../../../components/BrieChatMenu/BrieIconOutlined";

interface Props {
  module: Module;
  record?: any;
  onConfirm?: (result: any) => void;
}

const Recorder = new AudioRecorder();

/**
 * Brie Assistant Within Record Context
 */
export default function BrieRecordAssist({ module, record, onConfirm }: Props) {
  const [request, setRequest] = useState("");
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { fetchTranscript, deleteTranscript } = useTranscript();
  const { updateRecord, recordUpdating } = useUpdateRecord(false, record?.id);
  const fieldMap = useFieldMap(module?.APIName);

  if (isRecording) {
    return (
      <div className="d-flex align-items-center text-nowrap">
        <div className="text-sm flow-text font-weight-500">
          <span className="flow-text-inner">Brie is listening...</span>
        </div>
        <IconButton
          icon="stop"
          color="primary"
          onClick={onFinishRecording}
          className="mx-2"
        />
      </div>
    );
  }

  if (processing) {
    return <AppSpinner size="sm" className="mr-4" />;
  }

  return (
    <div className="position-relative">
      <Button
        size="sm"
        onClick={onStartRecording}
        className="p-0 px-2 mx-1"
        outline
      >
        <BrieIconOutlined />
      </Button>
      {error && (
        <Alert
          color="danger"
          className="small position-absolute py-1 pl-2 pr-4 d-flex"
          style={{
            width: 280,
            left: -95,
            top: 45,
            fontWeight: 500,
            fontSize: 11,
            zIndex: 999,
          }}
        >
          {error}
          <i
            className="mdi mdi-close position-absolute cursor-pointer"
            style={{ right: 5, top: 1, fontSize: 16 }}
            onClick={() => setError("")}
          />
        </Alert>
      )}
      <AppModal
        open={!!result}
        onClose={() => setResult(null)}
        title="Brie Live Assist Result"
        submitting={recordUpdating}
        disabled={false}
        onConfirm={
          record ? handleUpdateRecord : onConfirm ? handleConfirm : null
        }
        // @ts-ignore
        backdrop="static"
        content={
          <>
            <h3 className="mt--4">You Said</h3>
            <div className="text-sm p-2 bg-ultralight border rounded-sm font-weight-500 mb-4">
              "{request?.trim?.()} "
            </div>
            <h3>Result</h3>
            {!result?.values?.length && (
              <div className="text-sm border rounded-sm shadow-sm p-2 text-dark">
                No values to update. You may close this menu.
              </div>
            )}
            {result?.values?.map((r: any) => {
              const field = fieldMap?.[r?.fieldAPIName];

              if (!field) return null;

              return (
                <div
                  className="d-flex align-items-center justify-content-between border rounded-sm shadow-sm px-2 my-1"
                  key={field?.APIName}
                >
                  <div
                    style={{ width: 500, gap: 10 }}
                    className="text-truncate text-sm px-1 py-1 d-flex w-100 align-items-center"
                  >
                    Update {fieldMap?.[r?.fieldAPIName]?.Name} to
                    <div className="w-100 ml--1">
                      {/* @ts-ignore */}
                      <FieldFormatter
                        field={field}
                        value={r?.value}
                        moduleAPIName={module?.APIName}
                        record={record}
                        recordID={record?.id}
                        updateRecordCustom={handleValueChange}
                        updateRecordItemsCustom={handleItemsChange}
                        updateMultiTextCustom={handleMultiTextChange}
                        table={
                          ["Text", "LongText"].includes(field?.FieldType)
                            ? false
                            : true
                        }
                      />
                    </div>
                  </div>
                  <IconButton
                    icon="close"
                    color="danger"
                    onClick={() => removeUpdateField(r?.fieldAPIName)}
                  />
                </div>
              );
            })}

            {!!result?.errors?.length && (
              <h3 className="mt-4">Request Errors</h3>
            )}
            {result?.errors?.map((e: any, i: number) => {
              if (!e.message) return null;

              return (
                <Alert
                  color="warning"
                  className="px-3 py-1 mb-2"
                  style={{ fontWeight: 500 }}
                  key={i}
                >
                  {e.message}
                </Alert>
              );
            })}
          </>
        }
      />
    </div>
  );

  function handleConfirm() {
    if (onConfirm) {
      onConfirm(result);
    }
    setResult(null);
  }

  function handleUpdateRecord() {
    if (!result?.values?.length) return setResult(null);

    const payload = result?.values?.reduce((pload: any, item: any) => {
      pload[item?.fieldAPIName] = item?.value;
      return pload;
    }, {});

    updateRecord({
      field: { ReadOnly: false },
      moduleAPIName: module?.APIName,
      recordID: record?.id,
      payload: payload,
      onSuccess: () => setResult(null),
      _UserActivity: undefined,
    });
  }

  function removeUpdateField(fieldAPIName: string) {
    if (!fieldAPIName) return;

    setResult((prev: any) => {
      if (!prev?.values?.length) return prev;

      const newValues = prev.values.filter(
        (v: any) => v?.fieldAPIName !== fieldAPIName
      );

      return { ...prev, values: newValues };
    });
  }

  function handleValueChange({ field, payload, onSuccess }: any) {
    if (!field?.APIName || !payload) return;

    setResult((prev: any) => {
      const res = findWhere(prev?.values, { fieldAPIName: field?.APIName });

      if (!res || !prev) return prev;

      res.value = payload[field?.APIName];

      return { ...prev };
    });

    callFunctionIfExists(onSuccess);
  }

  // Location, ...
  function handleItemsChange({ fieldAPIName, payload }: any) {
    try {
      const res = findWhere(result?.values, { fieldAPIName: fieldAPIName });

      if (!payload?.length) return;

      res.value;

      for (const item of payload) {
        if (item?.action === "Add") {
          res.value.push(item.payload);
          continue;
        }

        res.value = res.value.filter((i: any) => i?.id !== item?.payload?.id);
      }
    } catch {
      return;
    }

    setResult((prev: any) => ({ ...prev, values: [...(prev?.values || [])] }));
  }

  // Email, Phone, URL
  function handleMultiTextChange({ fieldAPIName, payload }: any) {
    handleValueChange({
      field: { APIName: fieldAPIName },
      payload: { [fieldAPIName]: payload },
    });
  }

  function restart() {
    setIsRecording(false);
    Recorder.cancelRecording();
  }

  function onStartRecording() {
    Recorder.startRecording({
      setError,
      setIsRecording,
      restart,
      onSuccess: (audio) => {
        onFetchTranscript(audio);
      },
    });
  }

  function onFinishRecording() {
    setIsRecording(false);
    Recorder.finishRecording({});
  }

  // Fetches a transcript from the audio, calls process request on success.
  async function onFetchTranscript(audioBlob: Blob) {
    setProcessing(true);

    try {
      const transcriptFileData = await fetchTranscript({
        audioBlob,
        onTranscriptSuccess: (transcript) => processRequest(transcript),
        onError: (fileData) => {
          setError("Something went wrong...");
          deleteTranscript({ fileData: fileData });
        },
      });

      deleteTranscript({
        fileData: transcriptFileData,
      });
    } catch (err) {}
  }

  // - Set request text to Brie for processing.
  async function processRequest(request: string) {
    setRequest(request);
    setError("");

    try {
      const res = await AIToolsAPI.brieProcessRequest({
        Text: request,
        Type: "record",
        ModuleAPIName: module?.APIName,
      });

      const data = res.data.data.data;

      if (!data?.values?.length) return setResult(data);

      const newValues = [];

      // Handle any values that require additional steps.
      for (const v of data.values) {
        const field = fieldMap[v?.fieldAPIName];

        if (field?.FieldType === "Location") {
          try {
            const location = await handleLocationSelection(v?.value, true);

            newValues.push({
              ...v,
              value: [{ ...location, Primary: false, id: StrUtils.uuidv4() }],
            });
            continue;
          } catch {
            continue;
          }
        }

        if (["Date", "DateTime", "Time"].includes(field?.FieldType)) {
          try {
            const date = new Date(v?.value);

            date.setMinutes(date.getMinutes() + new Date().getTimezoneOffset());

            const newValue = date.toISOString();

            newValues.push({ ...v, value: newValue });
          } catch {}

          continue;
        }

        newValues.push(v);
      }

      data.values = newValues;

      setResult(res.data.data.data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setProcessing(false);
    }
  }
}
